/**
 * PostHog Analytics Events for FinchGPT (Agent Chat)
 *
 * Tracks usage patterns, feature adoption, and user behavior
 * to inform product decisions for the AI assistant.
 */

// ============================================
// Event Names
// ============================================

export const FINCHGPT_EVENTS = {
  // Session Events
  SESSION_STARTED: 'finchgpt_session_started',
  SESSION_ENDED: 'finchgpt_session_ended',

  // Thread Events
  THREAD_CREATED: 'finchgpt_thread_created',
  THREAD_SELECTED: 'finchgpt_thread_selected',
  THREAD_DELETED: 'finchgpt_thread_deleted',
  THREAD_RENAMED: 'finchgpt_thread_renamed',

  // Message Events
  MESSAGE_SENT: 'finchgpt_message_sent',
  MESSAGE_RECEIVED: 'finchgpt_message_received',
  CHAT_ABORTED: 'finchgpt_chat_aborted',

  // Feature Usage
  ATTACHMENT_UPLOADED: 'finchgpt_attachment_uploaded',
  CITATION_CLICKED: 'finchgpt_citation_clicked',
  WEB_SEARCH_TOGGLED: 'finchgpt_web_search_toggled',
  DEMAND_EXAMPLE_SELECTED: 'finchgpt_demand_example_selected',
  TOOL_CALL_EXPANDED: 'finchgpt_tool_call_expanded',

  // UI Interactions
  SIDEBAR_TOGGLED: 'finchgpt_sidebar_toggled',
  SIDEBAR_RESIZED: 'finchgpt_sidebar_resized',
  VIEW_MODE_CHANGED: 'finchgpt_view_mode_changed',
} as const;

// ============================================
// Property Keys
// ============================================

export const FINCHGPT_PROPERTIES = {
  // Context
  PAGE: 'page',
  COMPONENT: 'component',
  MATTER_ID: 'matter_id',
  THREAD_ID: 'thread_id',
  WORKSPACE_ID: 'workspace_id',

  // Session & Timing
  SESSION_DURATION_MS: 'session_duration_ms',
  MESSAGE_COUNT: 'message_count',

  // Window & Layout
  WINDOW_WIDTH: 'window_width',
  WINDOW_HEIGHT: 'window_height',
  SIDEBAR_WIDTH: 'sidebar_width',
  VIEW_MODE: 'view_mode', // 'compact' | 'fullscreen'
  IS_SIDEBAR_OPEN: 'is_sidebar_open',

  // Thread
  THREAD_COUNT: 'thread_count',
  THREAD_TITLE: 'thread_title',
  IS_NEW_THREAD: 'is_new_thread',

  // Message
  MESSAGE_LENGTH: 'message_length',
  HAS_ATTACHMENTS: 'has_attachments',
  ATTACHMENT_COUNT: 'attachment_count',
  HAS_WEB_SEARCH: 'has_web_search',

  // Tool Calls
  TOOL_NAME: 'tool_name',
  TOOL_STATUS: 'tool_status', // 'loading' | 'done' | 'error'

  // Features
  WEB_SEARCH_ENABLED: 'web_search_enabled',
  DEMAND_EXAMPLE_ID: 'demand_example_id',

  // Citation
  CITATION_ID: 'citation_id',
  CITATION_TYPE: 'citation_type',
} as const;

// ============================================
// Type Helpers
// ============================================

export type FinchGPTEvent =
  (typeof FINCHGPT_EVENTS)[keyof typeof FINCHGPT_EVENTS];

export type FinchGPTProperty =
  (typeof FINCHGPT_PROPERTIES)[keyof typeof FINCHGPT_PROPERTIES];

// ============================================
// Helper to get window dimensions
// ============================================

export const getWindowDimensions = () => ({
  [FINCHGPT_PROPERTIES.WINDOW_WIDTH]: window.innerWidth,
  [FINCHGPT_PROPERTIES.WINDOW_HEIGHT]: window.innerHeight,
});
